import { useEffect, useRef, useState } from 'react'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import invariant from 'tiny-invariant'
import { useTranslation } from '@kostbar/i18n'
import {
  AlertTriangle,
  GripVertical,
  HelpCircle,
  MoreHorizontal,
  PenLine,
  Store,
  XCircle,
} from 'lucide-react'
import { TILE_COLORS, tileColorClass } from '../constants'
import { TileStockBreakdown, derivedTileStock } from './tile-stock-breakdown'
import type { TileState } from '../constants'
import type { PosLayoutTile, Product } from '@/api/types'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Banner per tile state. `ready` and `pending` have none — a tile that is fine, and one we are
 * still resolving, both have nothing to report.
 */
const STATE_BANNERS: Partial<
  Record<
    TileState,
    { Icon: typeof AlertTriangle; className: string; labelKey: string }
  >
> = {
  'no-price': {
    Icon: AlertTriangle,
    className: 'bg-amber-500 text-white',
    labelKey: 'tileState.noPrice',
  },
  deactivated: {
    Icon: XCircle,
    className: 'bg-destructive text-destructive-foreground',
    labelKey: 'tileState.inactive',
  },
  'not-assigned': {
    Icon: Store,
    className: 'bg-slate-600 text-white',
    labelKey: 'tileState.notAssigned',
  },
  unresolved: {
    Icon: HelpCircle,
    className: 'bg-muted text-muted-foreground',
    labelKey: 'tileState.unresolved',
  },
  'open-price': {
    Icon: PenLine,
    className: 'bg-blue-500 text-white',
    labelKey: 'tileState.openPrice',
  },
}

interface LayoutTileProps {
  tile: PosLayoutTile
  product?: Product
  tileState: TileState
  /**
   * In-store available quantity (CHG-2026-09-pos-layout-stock-and-menu-diff). `undefined` means we
   * have no number — not tracked here, not resolved yet, or no permission to see it — and renders
   * as nothing at all rather than a zero.
   */
  availableQty?: number
  onRemove: (tileId: string) => void
  onColorChange: (tileId: string, color: string) => void
  onSetPrice: (sku: string) => void
  canEdit: boolean
}

export function LayoutTile({
  tile,
  product,
  tileState,
  availableQty,
  onRemove,
  onColorChange,
  onSetPrice,
  canEdit,
}: LayoutTileProps) {
  const { t: tPosLayout } = useTranslation('pos-layout')
  const tileRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!canEdit) return
    const el = tileRef.current
    invariant(el)
    return draggable({
      element: el,
      getInitialData: () => ({
        type: 'tile',
        tileId: tile.id,
        row: tile.row,
        col: tile.col,
      }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    })
  }, [tile, canEdit])

  // Absence of data is never a quantity: a tile we could not resolve, or one still resolving, has
  // nothing to say about stock — and neither does a restaurant that does not track this article.
  const showsStock =
    availableQty !== undefined &&
    tileState !== 'pending' &&
    tileState !== 'unresolved'

  // A dish or variant tile (CHG-2026-09-stock-usage-and-price-twins, D8, task 11.5): its number is
  // the in-store "enough for" over the tracked parts, with a chip naming the limiting part and a
  // breakdown one tap away. A plain article's tile carries no reading and renders as before.
  const derived =
    product && tileState !== 'pending' && tileState !== 'unresolved'
      ? derivedTileStock(product)
      : null

  const banner = STATE_BANNERS[tileState]
  // An empty part turns the tile red and names the part — unless a product fact already occupies
  // the banner, which stays the more important message.
  const stockBanner =
    banner === undefined && derived?.emptyPartLabel
      ? derived.emptyPartLabel
      : null
  const isWarning = banner !== undefined || stockBanner !== null
  // A product fact ("inactive", "not sold here") greys the tile out. A data state ("we could not
  // resolve this SKU") must not — it says nothing about the product.
  const isProductProblem =
    tileState === 'deactivated' || tileState === 'not-assigned'
  // Never offer to overwrite a price we could not read: the dialog prefills from the resolved
  // product, so an empty field on an unresolved tile invites silently replacing a correct price.
  const canSetPrice =
    tileState !== 'open-price' &&
    tileState !== 'unresolved' &&
    tileState !== 'pending'

  return (
    <div
      ref={tileRef}
      className={`group relative h-full w-full rounded-lg px-3 py-2 shadow-sm transition-opacity ${
        dragging ? 'opacity-60' : ''
      } ${tileColorClass(tile.color)} ${
        isProductProblem ? 'opacity-50 grayscale' : ''
      } ${tileState === 'pending' ? 'animate-pulse' : ''}`}
    >
      {banner && (
        <div
          className={`absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 rounded-b-lg px-2 py-1.5 text-xs font-semibold ${banner.className}`}
        >
          <banner.Icon className="h-3.5 w-3.5" />
          {tPosLayout(banner.labelKey)}
        </div>
      )}
      {stockBanner && (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 rounded-b-lg bg-red-900 px-2 py-1.5 text-xs font-semibold text-white">
          <AlertTriangle className="h-3.5 w-3.5" />
          {tPosLayout(stockBanner.key, { name: stockBanner.name })}
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
          <GripVertical
            className={`h-4 w-4 shrink-0 ${canEdit ? 'opacity-70' : 'opacity-30'}`}
          />
          {showsStock && derived && product ? (
            <TileStockBreakdown product={product} reading={derived} />
          ) : (
            showsStock &&
            (availableQty === 0 ? (
              <span className="rounded-md bg-pos-tile-alert px-1.5 py-0.5 text-xs font-semibold tabular-nums text-pos-tile-alert-foreground">
                0
              </span>
            ) : (
              <span className="text-xs font-semibold tabular-nums opacity-75">
                {formatStockDisplay(availableQty)}
              </span>
            ))
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-full p-1 text-inherit opacity-70 transition-opacity hover:opacity-100"
              aria-label={tPosLayout('actions.tileActionsAria')}
              disabled={!canEdit}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canSetPrice && (
              <>
                <DropdownMenuItem
                  onClick={() => onSetPrice(tile.sku)}
                  disabled={!canEdit}
                >
                  {tPosLayout('actions.setPrice')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {TILE_COLORS.map((color) => (
              <DropdownMenuItem
                key={color.key}
                onClick={() => onColorChange(tile.id, color.key)}
                disabled={!canEdit}
              >
                {tPosLayout(color.labelKey)}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onRemove(tile.id)}
              className="text-destructive"
              disabled={!canEdit}
            >
              {tPosLayout('actions.removeTile')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div
        className={`mt-3 text-lg font-bold leading-tight ${isWarning && !derived?.chip ? 'mb-6' : ''}`}
      >
        {product?.name || tile.label || tile.sku}
      </div>
      {tileState === 'ready' && product?.currentPrice !== undefined && (
        <div className="text-sm font-semibold">
          €{(product.currentPrice / 100).toFixed(2)}
        </div>
      )}
      {tileState === 'open-price' && (
        <div className="text-sm font-semibold">
          {tPosLayout('tileState.openPrice')}
        </div>
      )}
      {derived?.chip && (
        <div
          className={`mt-1 inline-block rounded-md bg-white/70 px-1.5 py-0.5 text-[11px] font-semibold text-foreground ${isWarning ? 'mb-6' : ''}`}
        >
          {tPosLayout(derived.chip.key, derived.chip.values)}
        </div>
      )}
    </div>
  )
}
